import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ChevronDown, Clock, Database, Hash, Play, Shuffle } from 'lucide-react';
import { useAuthStore } from '../store';
import { api, CscaRandomBlueprintResponse } from '../utils/api';

const CSCARandomPaper: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const navigate = useNavigate();
  const [data, setData] = useState<CscaRandomBlueprintResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    api.getCscaRandomBlueprint(token, 3)
      .then(response => { if (!cancelled) setData(response); })
      .catch((requestError: Error) => { if (!cancelled) setError(requestError.message || '无法生成随机组卷配置'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const allocatedTotal = useMemo(() => data?.topics.reduce((sum, topic) => sum + topic.question_count, 0) || 0, [data]);

  if (loading) return <div className="py-20 text-center font-semibold text-[#312E81]">正在根据生产 D1 中的五套往年卷计算48题配额…</div>;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl space-y-5">
        <Link to="/csca" className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5]"><ArrowLeft className="h-4 w-4" /> 返回 CSCA 数学</Link>
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700"><AlertCircle className="mt-0.5 h-5 w-5" /><div><div className="font-bold">随机组卷配置加载失败</div><div className="mt-1 text-sm">{error}</div></div></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-10">
      <Link to="/csca" className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5] hover:text-indigo-700"><ArrowLeft className="h-4 w-4" /> 返回 CSCA 数学</Link>

      <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#312E81] via-[#4338CA] to-[#7C3AED] p-6 text-white shadow-xl shadow-indigo-500/20 md:p-9">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-indigo-200"><Shuffle className="h-5 w-5" /> CSCA 数学 · 随机组卷</div>
            <h1 className="mt-3 text-3xl font-black md:text-4xl">按往年平均题量生成48题</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-indigo-100 md:text-base">先按五套往年卷的粗分类和细分频率分配48题，再按 question_id 最后三位匹配出题位置。每个位置从对应题池重新抽取1题。</p>
          </div>
          <button type="button" onClick={() => navigate('/csca/random/exam')} disabled={allocatedTotal !== 48} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-black text-[#4338CA] shadow-lg hover:bg-indigo-50 disabled:opacity-50"><Play className="h-5 w-5 fill-current" /> 开始随机组卷</button>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 text-center"><Database className="mx-auto h-5 w-5 text-indigo-500" /><div className="mt-2 text-2xl font-black text-[#312E81]">{data.source_paper_count}</div><div className="text-xs text-slate-500">套来源试卷</div></div>
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 text-center"><div className="text-2xl font-black text-[#312E81]">{data.source_question_count}</div><div className="text-xs text-slate-500">道来源题目</div></div>
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 text-center"><div className="text-2xl font-black text-[#312E81]">{allocatedTotal}</div><div className="text-xs text-slate-500">本卷题数</div></div>
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 text-center"><Clock className="mx-auto h-5 w-5 text-indigo-500" /><div className="mt-2 text-2xl font-black text-[#312E81]">60</div><div className="text-xs text-slate-500">分钟</div></div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        历史平均合计为47.8题，系统使用“最大余数法”转换为整数并确保总数正好48题。随后按原题号匹配位置，例如 CSCA_25_10_001 对应出题位置01。A–D选项保持题库原始顺序。
      </section>

      <section className="rounded-3xl border border-fuchsia-100 bg-white p-5 shadow-sm md:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-600"><Hash className="h-5 w-5" /></div>
          <div>
            <h2 className="font-black text-[#312E81]">出题位置 01–48</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">每个位置恰好1题。数字来自 question_id 最后三位；方格提示中可查看该位置在五套往年卷中的候选题数量。</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-8 gap-1.5 sm:grid-cols-12">
          {data.positions.map(item => (
            <div key={item.position} title={`位置 ${item.position_label}：${item.candidate_count} 道候选题`} className="rounded-lg border border-fuchsia-100 bg-fuchsia-50 py-2 text-center text-xs font-extrabold text-fuchsia-700">
              {item.position_label}
            </div>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-sm">
        <div className="grid grid-cols-[minmax(0,1fr)_90px_90px] gap-3 border-b border-indigo-100 bg-indigo-50 px-4 py-3 text-xs font-extrabold text-indigo-500 md:px-6">
          <span>粗分类</span><span className="text-center">历史平均</span><span className="text-center">本卷题数</span>
        </div>
        {data.topics.map(topic => (
          <details key={topic.coarse_type} className="group border-b border-slate-100 last:border-0">
            <summary className="grid cursor-pointer list-none grid-cols-[minmax(0,1fr)_90px_90px] items-center gap-3 px-4 py-4 hover:bg-indigo-50/50 md:px-6">
              <span className="flex min-w-0 items-center gap-2 font-extrabold text-[#312E81]"><ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />{topic.coarse_type}</span>
              <span className="text-center text-sm font-semibold text-slate-500">{topic.historical_average_per_paper}</span>
              <span className="text-center text-lg font-black text-[#4F46E5]">{topic.question_count}</span>
            </summary>
            <div className="bg-slate-50 px-4 py-3 md:px-10">
              {topic.fine_types.map(fine => (
                <div key={fine.fine_type} className="grid grid-cols-[minmax(0,1fr)_90px_90px] gap-3 border-b border-slate-200 py-2.5 text-sm last:border-0">
                  <span className="min-w-0 text-slate-700">{fine.fine_type}</span>
                  <span className="text-center text-slate-500">{fine.historical_average_per_paper}</span>
                  <span className="text-center font-extrabold text-[#4F46E5]">{fine.question_count}</span>
                </div>
              ))}
            </div>
          </details>
        ))}
      </section>

      <button type="button" onClick={() => navigate('/csca/random/exam')} disabled={allocatedTotal !== 48} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#4F46E5] px-6 py-4 text-lg font-black text-white shadow-lg hover:bg-indigo-700 disabled:opacity-50"><Shuffle className="h-5 w-5" /> 生成并开始48题随机试卷</button>
    </div>
  );
};

export default CSCARandomPaper;
